export type Phrase = {
  english: string;
  native: string;
  tip?: string;
};

export type PhraseCategory = {
  id: string;
  label: string;
  emoji: string;
  phrases: Phrase[];
};

const spanishCategories: PhraseCategory[] = [
  {
    id: "greetings",
    label: "Greetings",
    emoji: "👋",
    phrases: [
      { english: "Good morning", native: "Buenos días" },
      { english: "Good afternoon", native: "Buenas tardes" },
      { english: "Good evening / Good night", native: "Buenas noches" },
      { english: "Hello / Hi", native: "Hola" },
      { english: "How are you? (informal)", native: "¿Cómo estás?", tip: "Use with friends and family" },
      { english: "How are you? (formal)", native: "¿Cómo está usted?", tip: "Use with strangers, elders, or in professional settings" },
      { english: "Very well, thank you", native: "Muy bien, gracias" },
      { english: "Nice to meet you", native: "Mucho gusto" },
      { english: "What's your name?", native: "¿Cómo te llamas?" },
      { english: "My name is...", native: "Me llamo..." },
      { english: "Where are you from?", native: "¿De dónde eres?" },
      { english: "I'm from...", native: "Soy de..." },
      { english: "Goodbye", native: "Adiós" },
      { english: "See you later", native: "Hasta luego" },
      { english: "See you tomorrow", native: "Hasta mañana" },
      { english: "Please", native: "Por favor" },
      { english: "Thank you (very much)", native: "Muchas gracias" },
      { english: "You're welcome", native: "De nada" },
      { english: "Excuse me / Sorry", native: "Perdón / Lo siento" },
    ],
  },
  {
    id: "directions",
    label: "Getting Around",
    emoji: "🗺️",
    phrases: [
      { english: "Where is...?", native: "¿Dónde está...?" },
      { english: "How do I get to...?", native: "¿Cómo llego a...?" },
      { english: "Turn left", native: "Gire a la izquierda" },
      { english: "Turn right", native: "Gire a la derecha" },
      { english: "Go straight ahead", native: "Siga todo recto" },
      { english: "How far is it?", native: "¿A qué distancia está?" },
      { english: "How long does it take?", native: "¿Cuánto tiempo tarda?" },
      { english: "I'm lost", native: "Estoy perdido/a", tip: "Use perdido for men, perdida for women" },
      { english: "The bus stop", native: "La parada de autobús" },
      { english: "The metro station", native: "La estación de metro" },
      { english: "The airport", native: "El aeropuerto" },
      { english: "The train station", native: "La estación de tren" },
      { english: "A ticket to..., please", native: "Un billete a..., por favor" },
      { english: "Is this seat taken?", native: "¿Está ocupado este asiento?" },
    ],
  },
  {
    id: "food",
    label: "Food & Drink",
    emoji: "🍽️",
    phrases: [
      { english: "Can I have the menu, please?", native: "¿Me trae la carta, por favor?" },
      { english: "I would like to order...", native: "Quiero pedir..." },
      { english: "What do you recommend?", native: "¿Qué recomienda?" },
      { english: "The bill, please", native: "La cuenta, por favor" },
      { english: "Is service included?", native: "¿Está incluido el servicio?" },
      { english: "I'm vegetarian", native: "Soy vegetariano/a" },
      { english: "I'm vegan", native: "Soy vegano/a" },
      { english: "I'm allergic to...", native: "Soy alérgico/a a..." },
      { english: "Gluten-free, please", native: "Sin gluten, por favor" },
      { english: "A glass of water, please", native: "Un vaso de agua, por favor" },
      { english: "It's delicious!", native: "¡Está delicioso!" },
      { english: "Can I have some more bread?", native: "¿Me puede traer más pan?" },
      { english: "We'd like to share", native: "Lo queremos compartir" },
      { english: "For me, I'll have...", native: "Para mí, ..." },
    ],
  },
  {
    id: "shopping",
    label: "Shopping",
    emoji: "🛍️",
    phrases: [
      { english: "How much does it cost?", native: "¿Cuánto cuesta?" },
      { english: "It's too expensive", native: "Es demasiado caro" },
      { english: "Do you have anything cheaper?", native: "¿Tiene algo más barato?" },
      { english: "I'll take it", native: "Me lo llevo" },
      { english: "Can I pay by card?", native: "¿Puedo pagar con tarjeta?" },
      { english: "Do you accept cash?", native: "¿Aceptan efectivo?" },
      { english: "What size is this?", native: "¿Qué talla es esta?" },
      { english: "Do you have it in a different size?", native: "¿Lo tiene en otra talla?" },
      { english: "Do you have it in a different colour?", native: "¿Lo tiene en otro color?" },
      { english: "Can I try it on?", native: "¿Puedo probármelo?" },
      { english: "I'm just looking, thanks", native: "Solo estoy mirando, gracias" },
      { english: "Do you have this in stock?", native: "¿Tienen esto en stock?" },
    ],
  },
  {
    id: "emergencies",
    label: "Emergencies",
    emoji: "🚨",
    phrases: [
      { english: "Help!", native: "¡Ayuda!" },
      { english: "I need a doctor", native: "Necesito un médico" },
      { english: "Call an ambulance", native: "Llame a una ambulancia" },
      { english: "Call the police", native: "Llame a la policía" },
      { english: "I've been robbed", native: "Me han robado" },
      { english: "I've lost my passport", native: "He perdido mi pasaporte" },
      { english: "Where is the nearest hospital?", native: "¿Dónde está el hospital más cercano?" },
      { english: "Where is the pharmacy?", native: "¿Dónde está la farmacia?" },
      { english: "My head hurts", native: "Me duele la cabeza" },
      { english: "I feel sick", native: "Me siento mal" },
      { english: "I need a receipt / report", native: "Necesito un justificante / informe" },
      { english: "I don't understand", native: "No entiendo" },
      { english: "Do you speak English?", native: "¿Habla inglés?" },
      { english: "Please speak more slowly", native: "Por favor, hable más despacio" },
    ],
  },
  {
    id: "conversation",
    label: "Small Talk",
    emoji: "💬",
    phrases: [
      { english: "What do you think about...?", native: "¿Qué opinas de...?" },
      { english: "In my opinion...", native: "En mi opinión..." },
      { english: "I agree", native: "Estoy de acuerdo" },
      { english: "I disagree", native: "No estoy de acuerdo" },
      { english: "I don't know", native: "No sé" },
      { english: "Really? That's interesting", native: "¿En serio? Qué interesante" },
      { english: "How long have you been here?", native: "¿Cuánto tiempo llevas aquí?" },
      { english: "I've been here for... (days/months)", native: "Llevo aquí... (días/meses)" },
      { english: "What do you do for work?", native: "¿A qué te dedicas?" },
      { english: "I like it a lot", native: "Me gusta mucho" },
      { english: "I don't like it", native: "No me gusta" },
      { english: "It depends", native: "Depende" },
      { english: "No problem", native: "No hay problema" },
      { english: "Of course!", native: "¡Por supuesto! / ¡Claro!" },
      { english: "That's right", native: "Exacto / Así es" },
    ],
  },
];

const portugueseCategories: PhraseCategory[] = [
  {
    id: "greetings",
    label: "Greetings",
    emoji: "👋",
    phrases: [
      { english: "Good morning", native: "Bom dia" },
      { english: "Good afternoon", native: "Boa tarde" },
      { english: "Good evening / Good night", native: "Boa noite" },
      { english: "Hello / Hi", native: "Olá / Oi", tip: "'Oi' is more common in Brazil; 'Olá' works everywhere" },
      { english: "How are you?", native: "Como vai você? / Como está?", tip: "'Como vai?' is common in Brazil" },
      { english: "Very well, thank you", native: "Muito bem, obrigado/a", tip: "Men say obrigado, women say obrigada" },
      { english: "Nice to meet you", native: "Prazer em conhecer" },
      { english: "What's your name?", native: "Como você se chama? / Qual é o seu nome?" },
      { english: "My name is...", native: "Meu nome é... / Me chamo..." },
      { english: "Where are you from?", native: "De onde você é?" },
      { english: "I'm from...", native: "Sou de..." },
      { english: "Goodbye", native: "Tchau / Adeus", tip: "'Tchau' is informal, 'Adeus' is formal" },
      { english: "See you later", native: "Até logo" },
      { english: "See you tomorrow", native: "Até amanhã" },
      { english: "Please", native: "Por favor" },
      { english: "Thank you (very much)", native: "Muito obrigado/a" },
      { english: "You're welcome", native: "De nada" },
      { english: "Excuse me / Sorry", native: "Com licença / Desculpe" },
    ],
  },
  {
    id: "directions",
    label: "Getting Around",
    emoji: "🗺️",
    phrases: [
      { english: "Where is...?", native: "Onde fica...?" },
      { english: "How do I get to...?", native: "Como chego em...?" },
      { english: "Turn left", native: "Vire à esquerda" },
      { english: "Turn right", native: "Vire à direita" },
      { english: "Go straight ahead", native: "Siga em frente" },
      { english: "How far is it?", native: "Qual é a distância?" },
      { english: "How long does it take?", native: "Quanto tempo leva?" },
      { english: "I'm lost", native: "Estou perdido/a", tip: "Perdido for men, perdida for women" },
      { english: "The bus stop", native: "O ponto de ônibus" },
      { english: "The metro station", native: "A estação de metrô" },
      { english: "The airport", native: "O aeroporto" },
      { english: "The train station", native: "A estação de trem / comboios", tip: "'Comboios' is used in Portugal; 'trem' in Brazil" },
      { english: "A ticket to..., please", native: "Uma passagem para..., por favor" },
      { english: "Is this seat taken?", native: "Este lugar está ocupado?" },
    ],
  },
  {
    id: "food",
    label: "Food & Drink",
    emoji: "🍽️",
    phrases: [
      { english: "Can I have the menu, please?", native: "Pode trazer o cardápio, por favor?" },
      { english: "I would like to order...", native: "Gostaria de pedir..." },
      { english: "What do you recommend?", native: "O que você recomenda?" },
      { english: "The bill, please", native: "A conta, por favor" },
      { english: "Is service included?", native: "O serviço está incluso?" },
      { english: "I'm vegetarian", native: "Sou vegetariano/a" },
      { english: "I'm vegan", native: "Sou vegano/a" },
      { english: "I'm allergic to...", native: "Sou alérgico/a a..." },
      { english: "Gluten-free, please", native: "Sem glúten, por favor" },
      { english: "A glass of water, please", native: "Um copo d'água, por favor" },
      { english: "It's delicious!", native: "Está delicioso!" },
      { english: "Can I have some more bread?", native: "Pode trazer mais pão?" },
      { english: "For me, I'll have...", native: "Para mim, ..." },
    ],
  },
  {
    id: "shopping",
    label: "Shopping",
    emoji: "🛍️",
    phrases: [
      { english: "How much does it cost?", native: "Quanto custa?" },
      { english: "It's too expensive", native: "É muito caro" },
      { english: "Do you have anything cheaper?", native: "Tem algo mais barato?" },
      { english: "I'll take it", native: "Vou levar" },
      { english: "Can I pay by card?", native: "Posso pagar com cartão?" },
      { english: "Do you accept cash?", native: "Aceitam dinheiro?" },
      { english: "What size is this?", native: "Qual é o tamanho?" },
      { english: "Do you have it in a different size?", native: "Tem em outro tamanho?" },
      { english: "Do you have it in a different colour?", native: "Tem em outra cor?" },
      { english: "Can I try it on?", native: "Posso experimentar?" },
      { english: "I'm just looking, thanks", native: "Só estou olhando, obrigado/a" },
    ],
  },
  {
    id: "emergencies",
    label: "Emergencies",
    emoji: "🚨",
    phrases: [
      { english: "Help!", native: "Ajuda! / Socorro!" },
      { english: "I need a doctor", native: "Preciso de um médico" },
      { english: "Call an ambulance", native: "Chame uma ambulância" },
      { english: "Call the police", native: "Chame a polícia" },
      { english: "I've been robbed", native: "Fui roubado/a" },
      { english: "I've lost my passport", native: "Perdi meu passaporte" },
      { english: "Where is the nearest hospital?", native: "Onde fica o hospital mais próximo?" },
      { english: "Where is the pharmacy?", native: "Onde fica a farmácia?" },
      { english: "My head hurts", native: "Minha cabeça dói / Estou com dor de cabeça" },
      { english: "I feel sick", native: "Estou me sentindo mal" },
      { english: "I don't understand", native: "Não entendo" },
      { english: "Do you speak English?", native: "Você fala inglês?" },
      { english: "Please speak more slowly", native: "Por favor, fale mais devagar" },
    ],
  },
  {
    id: "conversation",
    label: "Small Talk",
    emoji: "💬",
    phrases: [
      { english: "What do you think about...?", native: "O que você acha de...?" },
      { english: "In my opinion...", native: "Na minha opinião..." },
      { english: "I agree", native: "Concordo" },
      { english: "I disagree", native: "Não concordo / Discordo" },
      { english: "I don't know", native: "Não sei" },
      { english: "Really? That's interesting", native: "Sério? Que interessante" },
      { english: "How long have you been here?", native: "Há quanto tempo você está aqui?" },
      { english: "What do you do for work?", native: "O que você faz? / Qual é a sua profissão?" },
      { english: "I like it a lot", native: "Gosto muito" },
      { english: "I don't like it", native: "Não gosto" },
      { english: "It depends", native: "Depende" },
      { english: "No problem", native: "Não tem problema" },
      { english: "Of course!", native: "Claro! / Com certeza!" },
      { english: "That's right", native: "Exato / É isso" },
      { english: "Cheers! (toast)", native: "Saúde!" },
    ],
  },
];

const finnishCategories: PhraseCategory[] = [
  {
    id: "greetings",
    label: "Greetings",
    emoji: "👋",
    phrases: [
      { english: "Hello / Hi", native: "Hei / Moi" },
      { english: "Good morning", native: "Hyvää huomenta" },
      { english: "Good evening", native: "Hyvää iltaa" },
      { english: "Good night", native: "Hyvää yötä" },
      { english: "How are you?", native: "Mitä kuuluu?", tip: "Literally 'What is heard?'" },
      { english: "I'm doing well, thanks", native: "Hyvin kiitos" },
      { english: "Nice to meet you", native: "Hauska tutustua" },
      { english: "What is your name?", native: "Mikä sinun nimesi on?" },
      { english: "My name is...", native: "Minun nimeni on..." },
      { english: "Where are you from?", native: "Mistä olet kotoisin?" },
      { english: "I'm from...", native: "Olen kotoisin..." },
      { english: "Goodbye", native: "Näkemiin" },
      { english: "See you later", native: "Nähdään myöhemmin" },
      { english: "Please", native: "Ole hyvä" },
      { english: "Thank you (very much)", native: "Kiitos (paljon)" },
      { english: "You're welcome", native: "Ole hyvä" },
      { english: "Excuse me / Sorry", native: "Anteeksi" },
      { english: "Yes / No", native: "Kyllä / Ei" },
    ],
  },
  {
    id: "directions",
    label: "Getting Around",
    emoji: "🗺️",
    phrases: [
      { english: "Where is...?", native: "Missä on...?" },
      { english: "How do I get to...?", native: "Miten pääsen...?" },
      { english: "Turn left", native: "Käänny vasemmalle" },
      { english: "Turn right", native: "Käänny oikealle" },
      { english: "Go straight ahead", native: "Mene suoraan" },
      { english: "I'm lost", native: "Olen eksyksissä" },
      { english: "The bus stop", native: "Bussipysäkki" },
      { english: "The train station", native: "Rautatieasema" },
      { english: "How far is it?", native: "Kuinka kaukana se on?" },
      { english: "A ticket to..., please", native: "Yksi lippu..., ole hyvä" },
      { english: "Does this bus go to...?", native: "Meneekö tämä bussi...?" },
    ],
  },
  {
    id: "food",
    label: "Food & Drink",
    emoji: "🍽️",
    phrases: [
      { english: "I would like...", native: "Haluaisin..." },
      { english: "The menu, please", native: "Ruokalista, ole hyvä" },
      { english: "What do you recommend?", native: "Mitä suosittelette?" },
      { english: "I'm vegetarian", native: "Olen kasvissyöjä" },
      { english: "It's delicious!", native: "Se on herkullista!" },
      { english: "The bill, please", native: "Lasku, ole hyvä" },
      { english: "A coffee, please", native: "Kahvi, ole hyvä" },
      { english: "Water, please", native: "Vettä, ole hyvä" },
      { english: "Cheers!", native: "Kippis!" },
    ],
  },
  {
    id: "shopping",
    label: "Shopping",
    emoji: "🛍️",
    phrases: [
      { english: "How much does this cost?", native: "Paljonko tämä maksaa?" },
      { english: "That's too expensive", native: "Se on liian kallis" },
      { english: "Do you have...?", native: "Onko teillä...?" },
      { english: "I'll take it", native: "Otan sen" },
      { english: "Can I pay by card?", native: "Voinko maksaa kortilla?" },
      { english: "Where is the nearest...?", native: "Missä on lähin...?" },
    ],
  },
  {
    id: "smalltalk",
    label: "Small Talk",
    emoji: "💬",
    phrases: [
      { english: "What do you think about...?", native: "Mitä mieltä olet...?" },
      { english: "In my opinion...", native: "Minun mielestäni..." },
      { english: "I agree", native: "Olen samaa mieltä" },
      { english: "I disagree", native: "Olen eri mieltä" },
      { english: "I don't know", native: "En tiedä" },
      { english: "Really? That's interesting", native: "Oikeasti? Se on mielenkiintoista" },
      { english: "What do you do for work?", native: "Mitä teet työksesi?" },
      { english: "I like it a lot", native: "Pidän siitä paljon" },
      { english: "I don't like it", native: "En pidä siitä" },
      { english: "Of course!", native: "Tietenkin!" },
      { english: "No problem", native: "Ei hätää" },
    ],
  },
];

export const phrasesByLanguage: Record<string, PhraseCategory[]> = {
  es: spanishCategories,
  pt: portugueseCategories,
  fi: finnishCategories,
};

export function getPhrasesForLanguage(languageCode: string): PhraseCategory[] {
  return phrasesByLanguage[languageCode] ?? spanishCategories;
}
